/**
 * Derive Client Snapshot "Score Insights" explanations from real assessment
 * fields on the live client/advice data object — not hardcoded UI blurbs.
 */

import {
  describeEsSelfAssessment,
  isB2cSelfAssessment,
} from "./esSelfAssessmentDisplay";

const ADL_FIELDS = [
  { key: "adlBathing", label: "bathing" },
  { key: "adlDressing", label: "dressing" },
  { key: "adlToileting", label: "toileting" },
  { key: "adlTransferring", label: "transferring" },
  { key: "adlContinence", label: "continence" },
  { key: "adlEating", label: "eating" },
];

const IADL_FIELDS = [
  { key: "iadlMedication", label: "medications" },
  { key: "iadlMeals", label: "meals" },
  { key: "iadlHousekeeping", label: "housekeeping" },
  { key: "iadlShopping", label: "shopping" },
  { key: "iadlFinances", label: "finances" },
  { key: "iadlTransportation", label: "transportation" },
  { key: "iadlCommunication", label: "communication" },
  { key: "iadlHomeMaintenance", label: "home maintenance" },
];

const ES_HAZARD_FIELDS = [
  { key: "esLooseRugs", label: "loose rugs" },
  { key: "esClutterWalkways", label: "cluttered walkways" },
  { key: "esPoorLighting", label: "poor lighting" },
  { key: "esStairsNoRailings", label: "stairs without railings" },
  { key: "esSlipperyFloors", label: "slippery floors" },
  { key: "esNoGrabBars", label: "missing grab bars" },
  { key: "esUnevenSteps", label: "uneven steps" },
  { key: "esWetSlipperyFloors", label: "wet/slippery floors" },
  { key: "esLayoutClutter", label: "layout clutter" },
  { key: "esObstructedPathways", label: "obstructed pathways" },
  { key: "esNoSmokeDetector", label: "no smoke detector" },
  { key: "esKitchenClutter", label: "kitchen clutter" },
];

const GAIT_LABELS = {
  5: "normal gait",
  4: "slow gait",
  3: "shuffling gait",
  2: "limping gait",
  1: "unsteady gait",
  0: "unable to walk",
};

const MOBILITY_LABELS = {
  4: "walks without device",
  3: "uses a cane",
  2: "uses a walker",
  1: "uses a wheelchair",
};

const isAssessed = (value) =>
  value !== undefined && value !== null && value !== "" && value !== "not-assessed";

const joinParts = (parts) =>
  parts.filter(Boolean).map((p) => String(p).trim()).filter(Boolean);

const describeFwb = (data = {}) => {
  const parts = [];
  [...ADL_FIELDS, ...IADL_FIELDS].forEach(({ key, label }) => {
    const v = data[key];
    if (v === "dependent") parts.push(`${label} dependent`);
    else if (v === "assistance") parts.push(`${label} needs assistance`);
  });

  const falls = Number(data.fallsLastYear);
  if (falls === 1) parts.push("2+ falls in the past year");
  else if (falls === 2) parts.push("1 fall in the past year");

  if (isAssessed(data.sleepQuality)) {
    parts.push(`sleep quality: ${data.sleepQuality}`);
  }

  const gait = Number(data.gaitSpeed);
  if (Number.isFinite(gait) && gait < 4 && GAIT_LABELS[gait]) {
    parts.push(GAIT_LABELS[gait]);
  }

  const mobility = Number(data.mobilityDevice);
  if (Number.isFinite(mobility) && mobility < 4 && MOBILITY_LABELS[mobility]) {
    parts.push(MOBILITY_LABELS[mobility]);
  }

  if (data.primaryCondition) {
    parts.push(`primary condition: ${data.primaryCondition}`);
  }
  if (data.secondaryCondition) {
    parts.push(`secondary condition: ${data.secondaryCondition}`);
  }

  return joinParts(parts);
};

const describeCt = (data = {}) => {
  const parts = [];
  const rel = Number(data.ctReliabilityPercent);
  if (Number.isFinite(rel) && rel > 0) {
    parts.push(`care reliability ${Math.round(rel)}%`);
  }

  const days = Number(data.ctDaysPerWeek ?? data.careDaysPerWeek);
  if (Number.isFinite(days) && days >= 0) {
    parts.push(`${Math.round(days)} care day${days === 1 ? "" : "s"} per week`);
  }

  const carePlan =
    data.ctCareplanCurrent === false ||
    data.carePlanStatus === "Outdated" ||
    data.carePlanStatus === "Not Current";
  if (carePlan) {
    parts.push("care plan not current");
  } else if (data.carePlanStatus === "Up to Date" || data.ctCareplanCurrent === true) {
    parts.push("care plan up to date");
  }

  const sat = Number(data.ctFamilyCommunication ?? data.familySatisfaction);
  if (Number.isFinite(sat) && sat > 0 && sat < 70) {
    parts.push(`family satisfaction ${Math.round(sat)}%`);
  }

  return joinParts(parts);
};

const describeEs = (data = {}) => {
  if (isB2cSelfAssessment(data)) {
    return describeEsSelfAssessment(data);
  }

  const flagged = ES_HAZARD_FIELDS.filter(({ key }) => data[key] === true);
  if (!flagged.length) return [];

  const labels = flagged.map((f) => f.label);
  if (labels.length <= 4) return joinParts(labels);
  return [
    `${labels.length} safety hazards flagged`,
    labels.slice(0, 3).join(", "),
  ];
};

const describeCategory = (areaId, data = {}) => {
  if (areaId === "wellbeing") return describeFwb(data);
  if (areaId === "careTeam") return describeCt(data);
  if (areaId === "environment") return describeEs(data);
  return [];
};

const formatExplanation = (area, detailParts) => {
  const badge = area.badge?.text || "—";
  const scoreLabel =
    area.score == null ? "no score recorded" : `${area.score}/100`;

  if (detailParts.length) {
    return detailParts.join("; ") + ".";
  }

  return `Score ${scoreLabel} (${badge}). Open View Details for the full assessment record.`;
};

/**
 * @param {object} params
 * @param {Array} params.snapshotAreas - from Client Snapshot cards
 * @param {Function} params.classifyArea - tier classifier from AipAnalysis
 * @param {object} params.data - merged client/assessment form data
 * @param {string} params.assessmentNotes - optional AIPAdvice.notes
 * @param {string} params.assessmentRecommendations - optional AIPAdvice.recommendations
 */
export const buildScoreInsights = ({
  snapshotAreas = [],
  classifyArea,
  data = {},
  assessmentNotes = "",
  assessmentRecommendations = "",
}) => {
  const flagged = snapshotAreas
    .map((area) => ({
      ...area,
      tier: classifyArea(area.score, area.esSource),
    }))
    .filter((area) => area.tier === "critical" || area.tier === "moderate");

  const categories = flagged.map((area) => {
    const detailParts = describeCategory(area.id, data);
    return {
      id: area.id,
      title: area.title,
      score: area.score,
      badge: area.badge,
      tier: area.tier,
      explanation: formatExplanation(area, detailParts),
      hasFieldDetail: detailParts.length > 0,
    };
  });

  const notes = String(assessmentNotes || data?.notes || "").trim();
  const recommendations = String(
    assessmentRecommendations || data?.recommendations || "",
  ).trim();

  return {
    categories,
    assessmentNotes: notes,
    assessmentRecommendations: recommendations,
    hasDocumentRecommendations: Boolean(recommendations),
  };
};
